import { useEffect, useState } from 'react'
import { Drawer, Pagination, Tag, Typography, message } from 'antd'
import { api } from '../api'
import type { KnowledgeScope } from '../constants/knowledgeScope'
import { listPagination, PageLoading, useServerPagination } from './ui'
import './VectorSearchChunkDrawer.css'

export type VectorChunkHit = {
  item_id: number
  chunk_index: number
  score: number
  snippet: string
  title?: string
}

type VectorChunksRes = {
  query: string
  item_id: number
  title: string
  chunks: VectorChunkHit[]
  total: number
  page: number
  page_size: number
  response_ms: number
}

type Props = {
  open: boolean
  onClose: () => void
  query: string
  scope: KnowledgeScope
  itemId: number | null
  docTitle: string
  topScore?: number
}

export function VectorSearchChunkDrawer({
  open,
  onClose,
  query,
  scope,
  itemId,
  docTitle,
  topScore,
}: Props) {
  const { page, pageSize, handlePaginationChange, paginationOptions, resetPage } = useServerPagination()
  const [loading, setLoading] = useState(false)
  const [total, setTotal] = useState(0)
  const [chunks, setChunks] = useState<VectorChunkHit[]>([])
  const [title, setTitle] = useState('')

  useEffect(() => {
    if (!open) return
    resetPage()
  }, [open, itemId, query, resetPage])

  useEffect(() => {
    if (!open || !itemId || !query.trim()) return
    setLoading(true)
    api
      .post<VectorChunksRes>('/public/search/vector-chunks', {
        query: query.trim(),
        item_id: itemId,
        scope,
        page,
        page_size: pageSize,
      })
      .then((r) => {
        setChunks(r.data.chunks ?? [])
        setTotal(r.data.total ?? 0)
        setTitle(r.data.title || docTitle)
      })
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }, [open, itemId, query, scope, page, pageSize, docTitle])

  const displayTitle = title || docTitle || `文档 ${itemId ?? ''}`
  const rankBase = (page - 1) * pageSize

  return (
    <Drawer
      className="vector-chunk-drawer"
      title={displayTitle}
      open={open}
      onClose={onClose}
      width={Math.min(720, typeof window !== 'undefined' ? window.innerWidth - 24 : 720)}
      destroyOnClose
    >
      <div className="vector-chunk-drawer-meta">
        <Typography.Text type="secondary">查询：{query}</Typography.Text>
        {topScore != null ? <Tag color="processing">最高相似度 {topScore.toFixed(2)}</Tag> : null}
        <Tag color="blue">共 {total} 段相似</Tag>
      </div>
      <Typography.Paragraph type="secondary" className="vector-chunk-drawer-hint">
        按相似度从高到低排列；仅包含本次向量召回窗口内命中的片段。
      </Typography.Paragraph>

      {loading ? (
        <PageLoading />
      ) : chunks.length === 0 ? (
        <Typography.Text type="secondary">暂无相似片段</Typography.Text>
      ) : (
        <ul className="vector-chunk-list">
          {chunks.map((c, idx) => (
            <li key={`${c.item_id}-${c.chunk_index}`} className="vector-chunk-item">
              <div className="vector-chunk-item-head">
                <span className="vector-chunk-rank">#{rankBase + idx + 1}</span>
                <Tag color="processing">相似度 {c.score.toFixed(2)}</Tag>
                <Tag>片段 {c.chunk_index}</Tag>
              </div>
              <p className="vector-chunk-snippet">{c.snippet || '（无正文）'}</p>
            </li>
          ))}
        </ul>
      )}

      {total > pageSize ? (
        <div className="vector-chunk-drawer-pagination">
          <Pagination
            {...listPagination(page, total, handlePaginationChange, { unit: '段', ...paginationOptions })}
          />
        </div>
      ) : null}
    </Drawer>
  )
}
